import { useState } from "react";
import { useJobStore } from "../../stores/jobStore";
import { ChartGallery } from "./ChartGallery";
import { ChartDetailPanel } from "./ChartDetailPanel";
import { PageAlbum } from "./PageAlbum";
import { AiIntakePanel } from "./AiIntakePanel";

const STATUS_CONFIG = {
  QUEUED:               { color: "text-slate-400",   bg: "bg-slate-500/10",   dot: "bg-slate-500" },
  RUNNING:              { color: "text-blue-400",    bg: "bg-blue-500/10",    dot: "bg-blue-500 animate-pulse" },
  INTAKE_COMPLETE:      { color: "text-cyan-400",    bg: "bg-cyan-500/10",    dot: "bg-cyan-500" },
  CATEGORIZED:          { color: "text-teal-400",    bg: "bg-teal-500/10",    dot: "bg-teal-500" },
  NEEDS_HUMAN_APPROVAL: { color: "text-amber-400",   bg: "bg-amber-500/10",   dot: "bg-amber-500 animate-pulse" },
  RESUMING:             { color: "text-violet-400",  bg: "bg-violet-500/10",  dot: "bg-violet-500 animate-pulse" },
  COMPLETED:            { color: "text-emerald-400", bg: "bg-emerald-500/10", dot: "bg-emerald-500" },
  FAILED:               { color: "text-red-400",     bg: "bg-red-500/10",     dot: "bg-red-500" },
  AI_INTAKE_PAUSED:     { color: "text-orange-400",  bg: "bg-orange-500/10",  dot: "bg-orange-500" },
} as Record<string, { color: string; bg: string; dot: string }>;

export function JobDetail() {
  const { selectedJob, charts, chartsLoading, liveEvents, selectedChart, intakeProgress, pages } = useJobStore();
  const [showEvents, setShowEvents] = useState(false);

  if (!selectedJob) return null;

  const cfg = STATUS_CONFIG[selectedJob.status] ?? STATUS_CONFIG.QUEUED;
  const needsReview = charts.filter((c) => c.status === "NEEDS_REVIEW").length;
  const done        = charts.filter((c) => ["COMPLETED", "APPROVED", "AUTO_APPROVED"].includes(c.status)).length;
  const failed      = charts.filter((c) => c.status === "FAILED").length;
  const total       = selectedJob.chart_count || charts.length;
  const pct         = total > 0 ? Math.round((done / total) * 100) : 0;

  const landingCount = charts.filter((c) => c.chart_category === "landing").length;
  const takeoffCount = charts.filter((c) => c.chart_category === "takeoff").length;
  const hasIntake    = landingCount > 0 || takeoffCount > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* ── Job header ─────────────────────────────────────────────────── */}
      <header className="flex-shrink-0 px-5 py-4 border-b border-rdp-border bg-rdp-card/50">
        <div className="flex items-start gap-4">
          {/* Title + id */}
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-white truncate" title={selectedJob.source_filename}>
              {selectedJob.source_filename}
            </h2>
            <p className="text-[11px] text-slate-500 font-mono mt-0.5">{String(selectedJob.id).slice(0, 16)}…</p>
          </div>

          {/* Status badge */}
          <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {selectedJob.status.replace(/_/g, " ")}
          </span>

          {/* Events toggle */}
          <button
            onClick={() => setShowEvents((v) => !v)}
            className={`btn-ghost flex-shrink-0 flex items-center gap-1.5 ${showEvents ? "text-white bg-rdp-active" : ""}`}
          >
            <span className="text-xs">⚡</span>
            Events
            {liveEvents.length > 0 && (
              <span className="bg-blue-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {liveEvents.length > 9 ? "9+" : liveEvents.length}
              </span>
            )}
          </button>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3">
          {/* Progress bar */}
          <div className="flex-1 space-y-1">
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>{done}/{total} charts complete</span>
              <span className="font-mono font-semibold text-slate-300">{pct}%</span>
            </div>
            <div className="h-1 bg-rdp-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Chips */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {hasIntake && landingCount > 0 && (
              <span className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                {landingCount} Landing
              </span>
            )}
            {hasIntake && takeoffCount > 0 && (
              <span className="text-[10px] font-bold bg-sky-500/15 text-sky-400 border border-sky-500/30 px-2 py-0.5 rounded-full">
                {takeoffCount} Takeoff
              </span>
            )}
            {needsReview > 0 && (
              <span className="text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full">
                {needsReview} HITL
              </span>
            )}
            {failed > 0 && (
              <span className="text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full">
                {failed} failed
              </span>
            )}
          </div>
        </div>
      </header>

      {/* ── Rule-based intake progress ────────────────────────────────── */}
      {intakeProgress && (
        <div className="flex-shrink-0 px-5 py-3 border-b border-rdp-border bg-rdp-bg/60">
          <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse inline-block" />
              Scanning PDF — page {intakeProgress.scanned} of {intakeProgress.total}
            </span>
            <span className="font-mono text-blue-400 font-semibold">
              {intakeProgress.chartsFound} charts found
            </span>
          </div>
          <div className="h-1.5 bg-rdp-border rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.round((intakeProgress.scanned / intakeProgress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* ── AI intake progress panel — shown for any AI-intake status ── */}
      {["RUNNING", "AI_INTAKE_PAUSED", "INTAKE_COMPLETE", "QUEUED"].includes(selectedJob.status) && (
        <AiIntakePanel />
      )}

      {/* ── Content ──────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 overflow-hidden">
          {chartsLoading ? (
            <div className="flex items-center justify-center h-32 gap-3 text-slate-500">
              <div className="w-5 h-5 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : charts.length > 0 ? (
            /* Chart gallery — preferred when charts exist */
            <ChartGallery charts={charts} />
          ) : pages.length > 0 ? (
            /* Page album — fallback for rule-based intake before charts are extracted */
            <PageAlbum />
          ) : (
            <ChartGallery charts={charts} />
          )}
        </div>

        {/* Chart detail panel */}
        {selectedChart && !showEvents && pages.length === 0 && <ChartDetailPanel />}

        {/* Live event log */}
        {showEvents && (
          <aside className="w-64 flex-shrink-0 border-l border-rdp-border bg-rdp-bg overflow-y-auto animate-slide-up">
            <div className="px-3 py-2 border-b border-rdp-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Live Events</p>
            </div>
            {liveEvents.length === 0 ? (
              <p className="text-xs text-slate-600 px-3 py-3">Waiting for events…</p>
            ) : (
              liveEvents.map((ev, i) => (
                <div key={i} className="px-3 py-2 border-b border-rdp-border/50 hover:bg-rdp-hover transition-colors">
                  <p className="text-[10px] font-mono text-blue-400 font-semibold">
                    {String(ev["event"] ?? "EVENT")}
                  </p>
                  {typeof ev["chart_id"] === "string" && (
                    <p className="text-[9px] text-slate-600 font-mono mt-0.5">
                      chart: {ev["chart_id"].slice(0, 8)}
                    </p>
                  )}
                </div>
              ))
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
